import { Check, AlertTriangle, Zap, Shield, CreditCard, MapPin } from 'lucide-react';

const problems = [
  {
    icon: AlertTriangle,
    title: 'Demoras en encontrar maquinaria',
    description: 'Los productores enfrentan largos plazos para conseguir equipos disponibles.'
  },
  {
    icon: AlertTriangle,
    title: 'Pagos informales',
    description: 'Falta de seguridad en transacciones y riesgo de incumplimiento.'
  },
  {
    icon: AlertTriangle,
    title: 'Sin referencias confiables',
    description: 'Difícil verificar la calidad y experiencia de los prestadores.'
  },
  {
    icon: AlertTriangle,
    title: 'Desorganización',
    description: 'Los prestadores luchan por encontrar nuevos clientes y organizar su agenda.'
  }
];

const solutions = [
  {
    icon: MapPin,
    title: 'Geolocalización en tiempo real',
    description: 'Encuentra maquinarias y prestadores cercanos disponibles al instante.'
  },
  {
    icon: Shield,
    title: 'Perfiles verificados',
    description: 'Sistema de calificaciones y reseñas para generar confianza.'
  },
  {
    icon: CreditCard,
    title: 'Pagos seguros en cuotas',
    description: 'Los productores pueden financiar y los prestadores cobran garantizado.'
  },
  {
    icon: Zap,
    title: 'Contratación inmediata',
    description: 'Desde tu celular, en segundos, sin papeleos ni esperas.'
  }
];

export const SolutionSection = () => {
  return (
    <section
      id="solucion"
      className="py-16 md:py-24 bg-[#FBF5EA]"
      data-testid="solution-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-sm font-bold uppercase tracking-[0.2em] text-[#AB6D4C] mb-4">
            Nuestra Solución
          </span>
          <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-[#051914] mb-6">
            ¿Qué es Prestagro?
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-[#051914]/70 max-w-3xl mx-auto">
            Una app móvil que digitaliza la contratación de servicios agropecuarios,
            eliminando la informalidad y agilizando toda la cadena agrícola.
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Image */}
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl bg-[#EBE9BD]">
              <img
                src="https://customer-assets.emergentagent.com/job_prestagro-agro/artifacts/l4jumdiy_heiko-janowski-XLmWt913EOc-unsplash.jpg"
                alt="Cosechadora y tractor trabajando en campo de trigo"
                className="w-full h-[500px] object-cover"
              />
            </div>
            {/* Floating Card */}
            <div className="absolute -bottom-6 -right-6 bg-[#051914] text-[#FBF5EA] p-6 rounded-2xl shadow-xl max-w-xs hidden md:block">
              <span className="text-3xl font-bold text-[#A9CB68]">100%</span>
              <p className="text-sm mt-1 text-[#FBF5EA]/80">
                Transacciones seguras con garantía de pago
              </p>
            </div>
          </div>

          {/* Content */}
          <div>
            <h3 className="text-2xl md:text-3xl font-medium text-[#051914] mb-6">
              Conectamos el campo argentino
            </h3>
            <p className="text-base md:text-lg leading-relaxed text-[#051914]/70 mb-8">
              Prestagro agrupa soluciones para productores y prestadores rurales. 
              Encuentra sembradoras, drones de fumigación, cosechadoras verificadas 
              mediante calificaciones de otros usuarios. Contrata y paga seguro en la app.
            </p>

            <div className="space-y-4">
              {solutions.map((solution, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-4 bg-[#EBE9BD]/50 rounded-2xl hover:bg-[#EBE9BD] transition-colors"
                  data-testid={`solution-item-${index}`}
                >
                  <div className="w-12 h-12 bg-[#A9CB68] rounded-xl flex items-center justify-center flex-shrink-0">
                    <solution.icon size={24} className="text-[#051914]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#051914] mb-1">
                      {solution.title}
                    </h4>
                    <p className="text-sm text-[#051914]/60">
                      {solution.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Problems Section */}
        <div className="bg-[#051914] rounded-3xl p-8 md:p-12">
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-medium text-[#FBF5EA] mb-4">
              Los desafíos del agro hoy
            </h3>
            <p className="text-[#FBF5EA]/70 max-w-2xl mx-auto">
              Miles de productores argentinos enfrentan estos problemas diariamente. 
              Prestagro nació para resolverlos.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problems.map((problem, index) => (
              <div
                key={index}
                className="bg-[#FBF5EA]/5 border border-[#FBF5EA]/10 rounded-2xl p-6 hover:bg-[#FBF5EA]/10 transition-colors"
                data-testid={`problem-item-${index}`}
              >
                <div className="w-10 h-10 bg-[#A9CB68]/20 rounded-xl flex items-center justify-center mb-4">
                  <problem.icon size={20} className="text-[#A9CB68]" />
                </div>
                <h4 className="font-medium text-[#FBF5EA] mb-2">
                  {problem.title}
                </h4>
                <p className="text-sm text-[#FBF5EA]/60">
                  {problem.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
